import { Box, styled, Tooltip, Typography, useTheme, Chip } from "@mui/material";
import useSession from "hooks/call-queue/useSession";
import { MicrophoneSlash, VideoSlash, User, Monitor } from "iconsax-react";
import { useEffect } from "react";
import { CallStatus } from "types/auth";
import Lottie from 'react-lottie';
import lottieDone from '../../../../assets/lottie/support-rating.json';

const VideoContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    backgroundColor: '#1a1a1a',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0',
    overflow: 'hidden',
    '& video': {
        width: '100%',
        height: '100%',
        // The phone streams portrait into this landscape tile. 'cover' cropped it
        // to fill and produced the unusably zoomed-in view support complained
        // about; the real switch is the `fit: 'contain'` passed to Agora's play()
        // (it writes object-fit inline), this just keeps the two in agreement.
        objectFit: 'contain',
        borderRadius: '0'
    },
    '@keyframes pulse': {
        '0%': {
            boxShadow: '0 0 0 0 rgba(63, 81, 181, 0.7)',
        },
        '70%': {
            boxShadow: '0 0 0 20px rgba(63, 81, 181, 0)',
        },
        '100%': {
            boxShadow: '0 0 0 0 rgba(63, 81, 181, 0)',
        }
    }
}));

const VideoOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.3) 100%)',
    pointerEvents: 'none',
    zIndex: 2
}));

const ParticipantInfo = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 16,
    left: 16,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(3),
    backdropFilter: 'blur(8px)'
}));

const StatusIndicator = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

const ConnectionStatus = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'connected'
})<{ connected?: boolean }>(({ connected }) => ({
    // Green only when genuinely connected. This was hardcoded green + "Connected",
    // so the badge claimed the call was up while the body still said Connecting.
    backgroundColor: connected ? 'rgba(76, 175, 80, 0.9)' : 'rgba(158, 158, 158, 0.9)',
    color: 'white',
    '& .MuiChip-icon': {
        color: 'white'
    }
}));

const VideoSession = () => {
    const { session, client, remoteVideo, reconnectVideoStreams } = useSession();
    const theme = useTheme();

    const defaultLottieOptions = {
        loop: false,
        autoplay: true, 
        animationData: lottieDone,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice'
        }
    };

    // NOTE: subscribing to remote users is VideoCall's job — it owns the client and
    // attaches its handler before join(). This component used to register a second
    // `user-published` handler and call removeAllListeners(), so the two silently
    // cancelled each other out depending on mount order. All that is left here is
    // re-attaching an already-subscribed track once the container exists.

    // Optimized video reattachment with debouncing
    useEffect(() => {
        // No status gate: #remote-video is always mounted now, and the track can
        // arrive before the call is marked IN_PROGRESS.
        if (!remoteVideo) return;
        
        let retryCount = 0;
        const maxRetries = 3;
        
        const attachVideo = () => {
            const videoElement = document.getElementById("remote-video");
            if (videoElement) {
                try {
                    remoteVideo.play("remote-video", { fit: "contain" });
                } catch (error) {
                    if (retryCount < maxRetries) {
                        retryCount++;
                        setTimeout(attachVideo, 200 * retryCount); // Exponential backoff
                    }
                }
            } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(attachVideo, 100 * retryCount);
            }
        };
        
        // Debounce the attachment
        const timeoutId = setTimeout(attachVideo, 50);
        return () => clearTimeout(timeoutId);
    }, [remoteVideo]);

    // Fallback for a track that was already subscribed before this view mounted.
    useEffect(() => {
        if (!client) return;

        if (!remoteVideo && client.remoteUsers.length > 0) {
            reconnectVideoStreams();
        }
    }, [client, remoteVideo, reconnectVideoStreams]);

    // What is actually happening, rather than a fixed label.
    //   Calling...   we have placed the call, no device has confirmed it rang
    //   Ringing...   the callee's device raised its incoming-call UI (ringingAt)
    //   Connecting.. answered; media still being set up
    //   Connected    both sides in the channel
    const phase = session?.status === CallStatus.IN_PROGRESS
        ? 'Connected'
        : session?.status === CallStatus.RECEIVED
            ? 'Connecting...'
            : session?.ringingAt
                ? 'Ringing...'
                : 'Calling...';

    const phaseHint = phase === 'Ringing...'
        ? "The user's device is ringing"
        : phase === 'Calling...'
            ? 'Waiting for the call to reach their device'
            : 'Please wait while we establish the connection';

    const getUserInitials = (name: string) => {
        if (!name?.trim()) return 'CU';
        const words = name.trim().split(/\s+/).slice(0, 2);
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return (words[0][0] + words[1][0]).toUpperCase();
    };

    return (
        <VideoContainer>
            <StatusIndicator>
                <ConnectionStatus 
                    icon={<Monitor size={16} />}
                    label={phase}
                    size="small"
                    connected={session?.status === CallStatus.IN_PROGRESS}
                />
            </StatusIndicator>

            <VideoOverlay />

            {/* ALWAYS mounted, never gated on call status.
              *
              * This used to render only while IN_PROGRESS, which created a race the
              * caller lost every time: as the outgoing caller we join the channel
              * during CALLING, so when the app published its camera there was no
              * #remote-video element for Agora to attach to and play() failed. The
              * call looked connected with a black tile - and refreshing "fixed" it
              * only because the page then loaded already IN_PROGRESS, container
              * first. Keeping it mounted removes the ordering problem entirely; the
              * status overlays below simply cover it until there is something to see.
              */}
            <Box 
                id="remote-video" 
                sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center'
                }} 
            />

            {session?.status === CallStatus.IN_PROGRESS && (
                <>
                    {session.userHidden && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            backgroundColor: 'rgba(26, 26, 26, 0.9)',
                            zIndex: 1
                        }}>
                            <Box sx={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2
                            }}>
                                <Typography variant="h3" color="white" fontWeight="bold">
                                    {getUserInitials(session?.fromName || 'Customer')}
                                </Typography>
                            </Box>
                            <Typography variant="h6" color="white" mb={1}>
                                {session?.fromName || 'Customer'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <VideoSlash size="20" color={theme.palette.grey[400]} />
                                <Typography variant="body2" color={theme.palette.grey[400]}>
                                    Camera is off
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <ParticipantInfo>
                        <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" color="white" fontWeight="bold">
                                {getUserInitials(session?.fromName || 'Customer')}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="white" fontWeight="medium">
                            {session?.fromName || 'Customer'}
                        </Typography>
                        {session.userMuted && (
                            <Tooltip title="Microphone is muted">
                                <MicrophoneSlash size="16" color={theme.palette.error.main} />
                            </Tooltip>
                        )}
                    </ParticipantInfo>
                </>
            )}

            {session?.status === CallStatus.COMPLETED && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1
                }}>
                    <Lottie 
                        options={defaultLottieOptions}
                        height={200}
                        width={200}
                    />
                    <Typography variant="h5" color="white" mt={2} mb={1}>
                        Session Completed
                    </Typography>
                    <Typography variant="body1" color={theme.palette.grey[400]} textAlign="center">
                        Thank you for your support!
                    </Typography>
                </Box>
            )}

            {session?.status === CallStatus.USER_LEFT && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1
                }}>
                    <Lottie 
                        options={defaultLottieOptions}
                        height={200}
                        width={200}
                    />
                    <Typography variant="h5" color="white" mt={2} mb={1}>
                        Call Ended
                    </Typography>
                    <Typography variant="body1" color={theme.palette.grey[400]} textAlign="center" mb={1}>
                        User left the room
                    </Typography>
                    <Typography variant="body2" color={theme.palette.grey[500]} textAlign="center">
                        You can stop recording & end the call
                    </Typography>
                </Box>
            )}

            {(session?.status !== CallStatus.IN_PROGRESS && 
              session?.status !== CallStatus.COMPLETED && 
              session?.status !== CallStatus.USER_LEFT) && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1
                }}>
                    <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        animation: 'pulse 2s infinite'
                    }}>
                        <User size="40" color="white" />
                    </Box>
                    <Typography variant="h6" color="white" mb={1}>
                        {phase}
                    </Typography>
                    <Typography variant="body2" color={theme.palette.grey[400]} textAlign="center">
                        {phaseHint}
                    </Typography>
                </Box>
            )}
        </VideoContainer>
    )
}

export default VideoSession;